import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Check, AlertCircle, Loader2, Upload } from "lucide-react";
import { translateError } from "@/lib/errors";

const inp = "w-full bg-[#111] border border-white/10 text-[#F5F5F7] text-sm px-3 py-1.5 focus:outline-none focus:border-[#C5A059] transition-colors placeholder-[#2a2a2a]";
const Lbl = ({ children }) => (
  <p className="text-[10px] tracking-widest text-[#666] uppercase mb-1">{children}</p>
);
const SecTitle = ({ children }) => (
  <p className="text-[#C5A059] text-[10px] tracking-[0.25em] uppercase mb-4 pb-2 border-b border-white/5">
    {children}
  </p>
);

const DEFAULTS = {
  company_name:   "Konyap Mühendislik",
  phone:          "+90 532 065 8519",
  email:          "konyapmuhendislik@gmail.com",
  address:        "Uluırmak Mah. Dr. Ahmet Özcan Cad. 179A Meram/KONYA",
  working_hours:  "Pzt - Cmt: 09:00 - 18:00",
  instagram_url:  "",
  facebook_url:   "",
  youtube_url:    "",
  linkedin_url:   "",
  logo_url:       "",
  favicon_url:    "",
};

// Kullanıcı adını tam URL'ye çevirir. Zaten URL ise dokunmaz.
const normalizeSocial = (value, baseUrl) => {
  const v = (value || "").trim();
  if (!v) return "";
  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  const username = v.startsWith("@") ? v.slice(1) : v;
  return `${baseUrl}${username}`;
};

const SOCIAL_BASES = {
  instagram_url: "https://www.instagram.com/",
  facebook_url:  "https://www.facebook.com/",
  youtube_url:   "https://www.youtube.com/@",
  linkedin_url:  "https://www.linkedin.com/company/",
};

const AdminCompany = () => {
  const [form,    setForm]    = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);
  const [logoUp,  setLogoUp]  = useState(false);

  useEffect(() => {
    supabase.from("company_settings").select("*").eq("id", 1).single()
      .then(({ data }) => {
        if (data) setForm({ ...DEFAULTS, ...data });
        setLoading(false);
      });
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLogoUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUp(field);
    try {
      const ext  = file.name.split(".").pop().toLowerCase();
      const name = `${field}_${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("project-assets").upload(`company/${name}`, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from("project-assets").getPublicUrl(`company/${name}`);
      setForm(prev => ({ ...prev, [field]: data.publicUrl }));
    } catch (err) {
      setMsg({ ok: false, text: translateError(err) });
    }
    setLogoUp(null);
    e.target.value = "";
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    const normalized = { ...form };
    for (const [field, base] of Object.entries(SOCIAL_BASES)) {
      normalized[field] = normalizeSocial(form[field], base);
    }
    const { error } = await supabase.from("company_settings")
      .upsert({
        id: 1,
        ...normalized,
        updated_at: new Date().toISOString(),
      });
    setMsg(error
      ? { ok: false, text: translateError(error) }
      : { ok: true,  text: "Kaydedildi." });
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#555] text-sm">
        <Loader2 size={15} className="animate-spin text-[#C5A059]" />
        Yükleniyor...
      </div>
    );
  }

  return (
    <div className="max-w-2xl pb-8">
      <div className="mb-8">
        <p className="text-[#C5A059] text-[10px] tracking-[0.25em] uppercase mb-1">Ayarlar</p>
        <h1 className="text-[#F5F5F7] text-2xl font-light">Şirket Bilgileri</h1>
      </div>

      <form onSubmit={handleSave} className="space-y-6">

        {/* İletişim */}
        <div className="bg-[#0a0a0a] border border-white/5 p-5 space-y-3">
          <SecTitle>İletişim Bilgileri</SecTitle>
          <div>
            <Lbl>Şirket Adı</Lbl>
            <input name="company_name" value={form.company_name} onChange={handleChange} className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Telefon</Lbl>
              <input name="phone" value={form.phone} onChange={handleChange} className={inp} placeholder="+90 532 000 0000" />
            </div>
            <div>
              <Lbl>E-posta</Lbl>
              <input name="email" value={form.email} onChange={handleChange} className={inp} placeholder="info@konyapmuhendislik.com" />
            </div>
          </div>
          <div>
            <Lbl>Adres</Lbl>
            <input name="address" value={form.address} onChange={handleChange} className={inp} />
          </div>
          <div>
            <Lbl>Çalışma Saatleri</Lbl>
            <input name="working_hours" value={form.working_hours} onChange={handleChange} className={inp} placeholder="Pzt - Cmt: 09:00 - 18:00" />
          </div>
        </div>

        {/* Sosyal Medya */}
        <div className="bg-[#0a0a0a] border border-white/5 p-5 space-y-3">
          <SecTitle>Sosyal Medya</SecTitle>
          {[
            { name: "instagram_url", label: "Instagram",  placeholder: "konyapmuhendislik  (veya tam URL)" },
            { name: "facebook_url",  label: "Facebook",   placeholder: "konyapmuhendislik  (veya tam URL)" },
            { name: "youtube_url",   label: "YouTube",    placeholder: "konyapmuhendislik  (veya tam URL)" },
            { name: "linkedin_url",  label: "LinkedIn",   placeholder: "konyapmuhendislik  (veya tam URL)" },
          ].map(({ name, label, placeholder }) => (
            <div key={name}>
              <Lbl>{label}</Lbl>
              <input name={name} value={form[name]} onChange={handleChange}
                className={inp} placeholder={placeholder} />
            </div>
          ))}
        </div>

        {/* Logo */}
        <div className="bg-[#0a0a0a] border border-white/5 p-5 space-y-4">
          <SecTitle>Logo</SecTitle>
          <p className="text-[#555] text-xs -mt-2">
            Yüklediğiniz logo; sitenin üst menüsünde, mobil menüde ve alt bölümde (footer) görünür.
            Boş bırakırsanız varsayılan logo kullanılmaya devam eder.
          </p>

          {/* Logo */}
          <div>
            <Lbl>Site Logosu</Lbl>
            <div className="flex gap-2">
              <input name="logo_url" value={form.logo_url} onChange={handleChange}
                className={`${inp} flex-1`} placeholder="https://... veya dosya yükleyin" />
              <label className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 text-xs text-[#555] hover:text-[#C5A059] hover:border-[#C5A059]/30 cursor-pointer transition-colors flex-shrink-0">
                {logoUp === "logo_url" ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                Yükle
                <input type="file" accept="image/png,image/svg+xml,image/webp,image/jpeg" className="sr-only"
                  onChange={e => handleLogoUpload(e, "logo_url")} />
              </label>
            </div>
            {form.logo_url ? (
              <div className="mt-2 flex items-center gap-3">
                <img src={form.logo_url} alt="Logo önizleme" className="h-12 object-contain bg-white/5 px-3 py-1"
                  onError={e => { e.target.style.display = "none"; }} />
                <button type="button" onClick={() => setForm(prev => ({ ...prev, logo_url: "" }))}
                  className="text-[11px] text-[#555] hover:text-red-400 transition-colors">
                  Kaldır (varsayılana dön)
                </button>
              </div>
            ) : (
              <p className="text-[11px] text-[#444] mt-1">Şu an varsayılan logo kullanılıyor.</p>
            )}
            <p className="text-[10px] text-[#333] mt-1">PNG veya SVG, şeffaf arka plan önerilir.</p>
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-50 text-black px-7 py-2 text-[11px] font-semibold tracking-widest uppercase transition-colors flex items-center gap-1.5"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
          {msg && (
            <div className={`flex items-center gap-1.5 text-xs ${msg.ok ? "text-green-400" : "text-red-400"}`}>
              {msg.ok ? <Check size={12} /> : <AlertCircle size={12} />}
              {msg.text}
            </div>
          )}
        </div>

      </form>
    </div>
  );
};

export default AdminCompany;
